import { useEffect, useRef, type RefObject } from 'react';

export type AxiMobileMenuToggleProps = {
  /** Whether the mobile menu is currently open. */
  open: boolean;
  /** Called when the hamburger button is tapped. */
  onToggle: () => void;
  /** Called to dismiss the menu (backdrop / ESC). */
  onClose: () => void;
  /**
   * Ref to an external trigger (if any). When the menu closes, focus
   * returns here; otherwise focus restores to the hamburger button.
   */
  triggerRef?: RefObject<HTMLButtonElement | null>;
};

/**
 * Mobile-only hamburger button + backdrop that opens/closes the
 * dashboard's sidebar as an overlay drawer. Visible only at viewport
 * widths `<= 768px` (the page-level `@media` block hides it on
 * desktop). Pattern mirrors `AxiSettingsPanel`: ESC closes, focus
 * returns to the trigger, backdrop click closes.
 *
 * The hamburger is `position: fixed` at top-left so it sits above the
 * Topbar user-bar (which is hidden on mobile via the same media block).
 */
function AxiMobileMenuToggle({
  open,
  onToggle,
  onClose,
  triggerRef
}: AxiMobileMenuToggleProps) {
  const fallbackRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      // Restore focus: prefer the external trigger, fall back to the
      // hamburger itself so keyboard users do not lose their place.
      triggerRef?.current?.focus();
      if (!triggerRef?.current) {
        fallbackRef.current?.focus();
      }
    };
  }, [open, onClose, triggerRef]);

  return (
    <>
      <button
        ref={fallbackRef}
        type="button"
        className="axi-mobile-menu-toggle"
        aria-label="打开主菜单"
        aria-expanded={open}
        aria-controls="axi-sidebar"
        onClick={onToggle}
      >
        <svg
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="4" y1="7" x2="20" y2="7" />
          <line x1="4" y1="12" x2="20" y2="12" />
          <line x1="4" y1="17" x2="20" y2="17" />
        </svg>
      </button>
      <div
        className={
          'axi-mobile-menu-backdrop' + (open ? ' is-open' : '')
        }
        onClick={onClose}
        aria-hidden="true"
      />
    </>
  );
}

export default AxiMobileMenuToggle;